
import React from 'react';
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Checkbox,
  Select,
  Button,
  Upload,
  Alert,
  DatePicker,
  Modal,
} from 'antd';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import type { RcFile } from 'antd/es/upload/interface';
import { useTranslate } from '@pankod/refine-core';
import { message } from 'antd';
import { ConveyanceStatus } from 'types/ownership/conveyanceStatus';
import { ConveyanceType } from 'types/ownership/conveyanceType';
import { ConveyanceAttachmentType } from 'types/ownership/conveyanceAttachmentType';
import { AttachmentValidationStatus } from 'types/ownership/attachmentValidationStatus';
import { CustomUploadFile } from 'types/ownership/customUploadFile';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';

interface ConveyanceFormProps {
  formConveyance: any;
  newConveyanceVisible: boolean;
  setNewConveyanceVisible: React.Dispatch<React.SetStateAction<boolean>>;
  newConveyanceError: boolean;
  setNewConveyanceError: React.Dispatch<React.SetStateAction<boolean>>;
  conveyanceStatus: ConveyanceStatus | undefined;
  setConveyanceStatus: React.Dispatch<React.SetStateAction<ConveyanceStatus | undefined>>;
  conveyanceAttachments: CustomUploadFile[];
  setConveyanceAttachments: React.Dispatch<React.SetStateAction<CustomUploadFile[]>>;
  conveyanceStatusOptions: { value: string; label: string }[];
  conveyanceTypeOptions: { value: string; label: string }[];
  attachmentTypeOptions: { value: string; label: string }[];
  attachmentStatusOptions: { value: string; label: string }[];
  selectPropsUser: any;
  users: { id: any; name: string; phone: string; email: string }[];
  onSearchUser: (searchText: string) => void;
  apiUrl: string;
  queryResult: any;
  authUser: any;
  newConveyanceHandleOk: () => Promise<void>;
  handleRemove: (file: UploadFile | CustomUploadFile, index?: number) => Promise<void>;
  ownerName?: string;
}

const beforeUpload = (file: RcFile) => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const isValidType = allowedTypes.includes(file.type);

  if (!isValidType) {
    message.error('Format non supporté !');
    return Upload.LIST_IGNORE;
  }

  const isLt5M = file.size / 1024 / 1024 < 5;
  if (!isLt5M) {
    message.error('Le fichier doit être inférieur à 5 Mo !');
  }

  return isValidType && isLt5M;
};

export const ConveyanceForm: React.FC<ConveyanceFormProps> = ({
  formConveyance,
  newConveyanceVisible,
  setNewConveyanceVisible,
  newConveyanceError,
  setNewConveyanceError,
  conveyanceStatus,
  setConveyanceStatus,
  conveyanceAttachments,
  setConveyanceAttachments,
  conveyanceStatusOptions,
  conveyanceTypeOptions,
  attachmentTypeOptions,
  attachmentStatusOptions,
  selectPropsUser,
  users,
  onSearchUser,
  apiUrl,
  queryResult,
  authUser,
  newConveyanceHandleOk,
  handleRemove,
  ownerName: propOwnerName,
}) => {
  const t = useTranslate();
  const { TextArea } = Input;

  const newConveyanceHandleCancel = () => {
    formConveyance.resetFields();
    setConveyanceAttachments([]);
    setNewConveyanceVisible(false);
  };

  const conveyanceHandleChange =
    (index: number) =>
    ({ fileList: newFileList }: UploadChangeParam<UploadFile>) => {
      const updatedFileList: CustomUploadFile[] = newFileList.map((file) => {
        if (file.response && file.response.filePath) {
          return {
            ...file,
            url: process.env.REACT_APP_SERVER + file.response.filePath,
            path: file.response.filePath,
            status: 'done' as const,
            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
            custom_type: null,
          } as CustomUploadFile;
        }
        return {
          ...file,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        } as CustomUploadFile;
      });

      const currentAttachments = formConveyance.getFieldValue('attachments') || [];
      currentAttachments[index] = {
        ...currentAttachments[index],
        path: updatedFileList[0]?.path || '',
        conveyance_type: updatedFileList[0]?.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        custom_type: updatedFileList[0]?.custom_type || null,
        details: updatedFileList[0]?.details,
        document_number: updatedFileList[0]?.document_number,
        issue_date: updatedFileList[0]?.issue_date,
        expiration_date: updatedFileList[0]?.expiration_date,
        validation_status: updatedFileList[0]?.validation_status || AttachmentValidationStatus.waiting,
        observations: updatedFileList[0]?.observations,
      };
      formConveyance.setFieldsValue({ attachments: currentAttachments });
      setConveyanceAttachments((prev) => {
        const newAttachments = [...prev];
        newAttachments[index] = updatedFileList[0] || {
          uid: `attach-${index}-${Date.now()}`,
          name: '',
          status: 'error' as const,
          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
          custom_type: null,
        };
        return newAttachments;
      });
    };

  const handleRemoveAttachment = (name: number, index: number, remove: (index: number) => void) => {
    const file = conveyanceAttachments[index];
    const conveyanceId = formConveyance.getFieldValue('id');

    setConveyanceAttachments((prev) => {
      const newAttachments = [...prev];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
    remove(name);
    message.success(t('notifications.deleteSuccess', { ns: 'common' }));

    if (file?.path && conveyanceId) {
      handleRemove(file, index).catch((err) => {
        console.error('Erreur lors de la suppression serveur:', err);
        message.error("Échec de la suppression de l'attachment côté serveur.");
      });
    }
  };

  const generatePDF = async () => {
    try {
      await formConveyance.validateFields();
      const formData = formConveyance.getFieldsValue();
      const attachments = formData.attachments || [];
      const contractor = users.find((user) => user.id === formData.grant_to);

      // Log pour déboguer les données disponibles
      console.log('queryResult:', JSON.stringify(queryResult, null, 2));
      console.log('formData:', JSON.stringify(formData, null, 2));
      console.log('propOwnerName:', propOwnerName);
      console.log('authUser:', JSON.stringify(authUser, null, 2));
      console.log('contractor:', JSON.stringify(contractor, null, 2));

      // Récupérer owner_name depuis propOwnerName, formData ou queryResult
      const owner = users.find((user) => user.id === queryResult?.data?.data?.user_id);
      const ownerName = propOwnerName || formData.owner_name || owner?.name || queryResult?.data?.data?.owner_name || 'Propriétaire inconnu';
      console.log('ownerName:', ownerName);

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Définir les styles
      const margin = 15;
      const pageWidth = 210;
      const pageHeight = 297;
      const lineHeight = 8;

      // Fonction pour tronquer les textes longs
      const truncateText = (text: string, maxLength: number) => {
        return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
      };

      // Générer un numéro de document unique
      const documentNumber = `CONV-${dayjs().format('YYYYMMDDHHmmss')}`;

      // En-tête avec placeholder pour le logo et numéro de document
      doc.setFillColor(0, 51, 102);
      doc.rect(0, 0, pageWidth, 20, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('STRIDE IMMO', margin, 12);
      doc.setFontSize(12);
      doc.text(`Document N°: ${documentNumber}`, pageWidth - margin, 12, { align: 'right' });

      // Titre
      let yPosition = 30;
      doc.setFontSize(20);
      doc.setTextColor(0, 51, 102);
      const headerText = t('fields.new_conveyance', { ns: 'conveyances' }, 'Nouveau Contrat');
      doc.text(headerText, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Généré le ${dayjs().format('DD.MM.YYYY')}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Section des parties impliquées
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.parties_involved', { ns: 'conveyances' }, 'Parties impliquées'), margin, yPosition);
      yPosition += 5;
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Tableau des parties : Propriétaire, Contractant, Intermédiaire
      const partiesBody = [
        [
          t('fields.owner_name', { ns: 'conveyances' }, 'Propriétaire'),
          truncateText(ownerName, 25),
          t('fields.grant_to_name', { ns: 'conveyances' }, 'Contractant'),
          truncateText(contractor?.name || formData.grant_to_name || 'N/A', 25),
          t('fields.intermediary', { ns: 'conveyances' }, 'Intermédiaire'),
          truncateText(`${authUser.lastname || 'N/A'} ${authUser.firstname || ''}`, 25),
        ],
        [
          t('fields.phone', { ns: 'conveyances' }, 'Téléphone'),
          truncateText(owner?.phone || 'N/A', 15),
          t('fields.phone', { ns: 'conveyances' }, 'Téléphone'),
          truncateText(contractor?.phone || 'N/A', 15),
          t('fields.phone', { ns: 'conveyances' }, 'Téléphone'),
          truncateText(authUser.phone || 'N/A', 15),
        ],
        [
          t('fields.email', { ns: 'conveyances' }, 'Email'),
          truncateText(owner?.email || 'N/A', 25),
          t('fields.email', { ns: 'conveyances' }, 'Email'),
          truncateText(contractor?.email || 'N/A', 25),
          t('fields.email', { ns: 'conveyances' }, 'Email'),
          truncateText(authUser.email || 'N/A', 25),
        ],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [
          [
            t('fields.owner', { ns: 'conveyances' }, 'Propriétaire'),
            '',
            t('fields.contractor', { ns: 'conveyances' }, 'Contractant'),
            '',
            t('fields.intermediary', { ns: 'conveyances' }, 'Intermédiaire'),
            '',
          ],
        ],
        body: partiesBody,
        theme: 'striped',
        headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontSize: 10 },
        bodyStyles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        margin: { left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 25 },
          5: { cellWidth: 35 },
        },
        didParseCell: (data) => {
          if (data.section === 'body' && [1, 3, 5].includes(data.column.index)) {
            data.cell.styles.overflow = 'linebreak';
          }
        },
        willDrawPage: (data) => {
          if (data.cursor && data.cursor.y > pageHeight - 40) {
            doc.addPage();
            if (data.cursor) {
              data.cursor.y = 20;
            }
          }
        },
      });
      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Section des détails du contrat
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.contract_details', { ns: 'conveyances' }, 'Détails du Contrat'), margin, yPosition);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Tableau des détails du contrat
      const contractDetailsBody = [
        [t('fields.title', { ns: 'conveyances' }, 'Titre'), formData.title || 'N/A'],
        [
          t('fields.type', { ns: 'conveyances' }, 'Type'),
          formData.type
            ? (conveyanceTypeOptions.find((opt) => opt.value === formData.type)?.label || formData.type)
            : 'N/A',
        ],
        [
          t('fields.status', { ns: 'conveyances' }, 'Statut'),
          formData.status
            ? (conveyanceStatusOptions.find((opt) => opt.value === formData.status)?.label || formData.status)
            : 'N/A',
        ],
        [
          t('fields.amount', { ns: 'conveyances' }, 'Prix'),
          formData.amount != null ? formData.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' FCFA' : '0 FCFA',
        ],
        [
          t('fields.interval', { ns: 'conveyances' }, 'Intervalle'),
          formData.interval != null ? String(formData.interval) : '0',
        ],
        [
          t('fields.start', { ns: 'conveyances' }, 'Date de début'),
          formData.start ? dayjs(formData.start).format('DD.MM.YYYY') : 'N/A',
        ],
        [
          t('fields.end', { ns: 'conveyances' }, 'Date d\'expiration'),
          formData.end ? dayjs(formData.end).format('DD.MM.YYYY') : 'N/A',
        ],
        [
          t('fields.auto_renew', { ns: 'conveyances' }, 'Renouvellement automatique'),
          formData.auto_renew
            ? t('yes', { ns: 'common' }, 'Oui')
            : t('no', { ns: 'common' }, 'Non'),
        ],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [[t('fields.field', { ns: 'conveyances' }, 'Champ'), t('fields.value', { ns: 'conveyances' }, 'Valeur')]],
        body: contractDetailsBody,
        theme: 'striped',
        headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontSize: 10 },
        bodyStyles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        margin: { left: margin, right: margin },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: pageWidth - 2 * margin - 60 } },
      });
      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Description et Note
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.description', { ns: 'conveyances' }, 'Description'), margin, yPosition);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const descriptionLines = doc.splitTextToSize(formData.description || 'N/A', pageWidth - 2 * margin);
      descriptionLines.forEach((line: string) => {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
      yPosition += 10;

      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.note', { ns: 'conveyances' }, 'Note'), margin, yPosition);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const noteLines = doc.splitTextToSize(formData.note || 'N/A', pageWidth - 2 * margin);
      noteLines.forEach((line: string) => {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
      yPosition += 15;

      // Section des pièces jointes
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.attachments', { ns: 'attachments' }, 'Pièces jointes'), margin, yPosition);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      if (attachments.length === 0) {
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(t('messages.no_attachments', { ns: 'conveyances' }, 'Aucune pièce jointe'), margin, yPosition);
        yPosition += 15;
      } else {
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        attachments.forEach((attachment: any, index: number) => {
          const attachmentTypeLabel =
            attachmentTypeOptions.find((opt) => opt.value === attachment.conveyance_type)?.label ||
            attachment.custom_type || 'N/A';
          const attachmentDetails = [
            `${t('type', { ns: 'attachments' }, 'Type')}: ${attachmentTypeLabel}`,
            `${t('fields.attachment_file', { ns: 'attachments' }, 'Document')}: ${conveyanceAttachments[index]?.name || 'N/A'}`,
            `${t('fields.details', { ns: 'attachments' }, 'Détails')}: ${attachment.details || 'N/A'}`,
            `${t('fields.document_number', { ns: 'attachments' }, 'Numéro du document')}: ${attachment.document_number || 'N/A'}`,
            `${t('fields.issue_date', { ns: 'attachments' }, "Date d'émission")}: ${attachment.issue_date ? dayjs(attachment.issue_date).format('DD.MM.YYYY') : 'N/A'}`,
            `${t('fields.expiration_date', { ns: 'attachments' }, "Date d'expiration")}: ${attachment.expiration_date ? dayjs(attachment.expiration_date).format('DD.MM.YYYY') : 'N/A'}`,
            `${t('fields.validation_status', { ns: 'attachments' }, 'Statut de validation')}: ${attachmentStatusOptions.find((opt) => opt.value === attachment.validation_status)?.label || attachment.validation_status || 'N/A'}`,
            `${t('fields.observations', { ns: 'attachments' }, 'Observations')}: ${attachment.observations || 'N/A'}`,
          ];

          doc.text(`Pièce jointe ${index + 1}:`, margin, yPosition);
          yPosition += lineHeight;
          attachmentDetails.forEach((line) => {
            if (yPosition > pageHeight - 40) {
              doc.addPage();
              yPosition = 20;
            }
            const splitLine = doc.splitTextToSize(line, pageWidth - 2 * margin);
            splitLine.forEach((subLine: string) => {
              doc.text(subLine, margin + 5, yPosition);
              yPosition += lineHeight;
            });
          });
          yPosition += 5;
        });
        yPosition += 10;
      }

      // Section des signatures
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(t('fields.signatures', { ns: 'conveyances' }, 'Signatures'), margin, yPosition);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(t('fields.owner_signature', { ns: 'conveyances' }, 'Signature du Propriétaire'), margin, yPosition);
      doc.text(t('fields.contractor_signature', { ns: 'conveyances' }, 'Signature du Contractant'), pageWidth / 2 - 20, yPosition);
      doc.text(t('fields.intermediary_signature', { ns: 'conveyances' }, 'Signature de l\'Intermédiaire'), pageWidth - margin - 60, yPosition);
      yPosition += 60;

      // Pied de page
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Généré par STRIDE IMMO le ${dayjs().format('DD.MM.YYYY HH:mm')}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Sauvegarder le PDF
      const fileName = formData.title ? `${formData.title}-${documentNumber}.pdf` : `contrat-${documentNumber}.pdf`;
      doc.save(fileName);
      message.success(t('messages.conveyance_created_success', { ns: 'conveyances' }, 'PDF généré et téléchargé avec succès !'));
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      message.error(t('messages.attachment_delete_failed', { ns: 'conveyances' }, 'Erreur lors de la génération du PDF.'));
    }
  };

  return (
    <Modal
      title={t('fields.new_conveyance', { ns: 'ownerships' })}
      visible={newConveyanceVisible}
      onOk={newConveyanceHandleOk}
      onCancel={newConveyanceHandleCancel}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('save')}
      width={1000}
      footer={[
        <Button key="generate-pdf" type="default" onClick={generatePDF} style={{ marginRight: 8 }}>
          Générer PDF
        </Button>,
        <Button key="cancel" onClick={newConveyanceHandleCancel}>
          {t('cancel')}
        </Button>,
        <Button key="submit" type="primary" onClick={newConveyanceHandleOk}>
          {t('save')}
        </Button>,
      ]}
    >
      {newConveyanceError && (
        <Alert
          message={t('fields.conveyance_error', { ns: 'ownerships' })}
          type="error"
          showIcon
          closable
        />
      )}
      <Form form={formConveyance} name="new_conveyance_form" layout="vertical">
        <Form.Item hidden name="id">
          <InputNumber hidden />
        </Form.Item>
        <Form.Item hidden name="owner_name" initialValue={propOwnerName || queryResult?.data?.data?.owner_name}>
          <Input hidden />
        </Form.Item>
        <Row>
          <Col className="gutter-row" span={24} style={{ marginBottom: '8px' }}>
            <Form.Item name="auto_renew" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.auto_renew', { ns: 'conveyances' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.title', { ns: 'conveyances' })}
              name="title"
              rules={[{ required: true, message: 'Veuillez saisir le titre' }]}
            >
              <Input maxLength={255} disabled={conveyanceStatus === ConveyanceStatus.Granted} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.type', { ns: 'conveyances' })}
              name="type"
              rules={[{ required: true, message: 'Veuillez sélectionner le type' }]}
            >
              <Select
                options={conveyanceTypeOptions}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.grant_to', { ns: 'conveyances' })}
              name="grant_to"
              rules={[{ required: true, message: 'Veuillez sélectionner le bénéficiaire' }]}
            >
              <Select
                {...selectPropsUser}
                options={users.map((user) => ({
                  value: user.id,
                  label: user.name,
                }))}
                onSearch={onSearchUser}
                showSearch
                filterOption={false}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.status', { ns: 'conveyances' })}
              name="status"
              rules={[{ required: true, message: 'Veuillez sélectionner le statut' }]}
            >
              <Select
                options={conveyanceStatusOptions}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
                onChange={(value) => setConveyanceStatus(value)}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.amount', { ns: 'conveyances' })}
              name="amount"
              rules={[{ required: true, message: 'Veuillez saisir le montant' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.interval', { ns: 'conveyances' })}
              name="interval"
              rules={[{ required: true, message: 'Veuillez saisir l\'intervalle' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.start', { ns: 'conveyances' })}
              name="start"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
              rules={[{ required: true, message: 'Veuillez saisir la date de début' }]}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                disabled={conveyanceStatus === ConveyanceStatus.Granted}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.end', { ns: 'conveyances' })}
              name="end"
              getValueProps={(value: any) => ({
                value: value ? dayjs(value) : null,
              })}
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={12} xl={12}>
            <Form.Item
              label={t('fields.description', { ns: 'conveyances' })}
              name="description"
            >
              <TextArea
                maxLength={255}
                rows={3}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.Item
              label={t('fields.note', { ns: 'conveyances' })}
              name="note"
            >
              <TextArea />
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={24}>
            <Form.List name="attachments">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row
                      key={key}
                      gutter={[16, 16]}
                      style={{
                        marginBottom: '16px',
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '16px',
                      }}
                    >
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.attachment_file', { ns: 'attachments' })}
                          name={[name, 'path']}
                          rules={[{ required: true, message: 'Veuillez télécharger un fichier' }]}
                        >
                          <Upload
                            action={`${apiUrl}/media/upload`}
                            data={{ table: `ownerships/${queryResult?.data?.data?.id}/conveyance` }}
                            listType="text"
                            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp"
                            onChange={conveyanceHandleChange(index)}
                            onRemove={() => {
                              handleRemoveAttachment(name, index, remove);
                              return false;
                            }}
                            fileList={
                              conveyanceAttachments[index] ? [conveyanceAttachments[index]] : []
                            }
                            maxCount={1}
                            beforeUpload={beforeUpload}
                          >
                            {(!conveyanceAttachments[index] ||
                              conveyanceAttachments[index].status !== 'done') && (
                              <Button style={{ borderRadius: '6px', padding: '4px 15px' }}>
                                Télécharger
                              </Button>
                            )}
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('type', { ns: 'attachments' })}
                          name={[name, 'conveyance_type']}
                          rules={[{ required: true, message: 'Veuillez sélectionner le type' }]}
                        >
                          <Select
                            options={attachmentTypeOptions}
                            onChange={(value) => {
                              const currentAttachments =
                                formConveyance.getFieldValue('attachments') || [];
                              currentAttachments[index] = {
                                ...currentAttachments[index],
                                conveyance_type: value,
                                custom_type:
                                  value !== ConveyanceAttachmentType.Autre
                                    ? null
                                    : currentAttachments[index].custom_type,
                              };
                              formConveyance.setFieldsValue({ attachments: currentAttachments });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      {(() => {
                        const attachmentType = formConveyance.getFieldValue([
                          'attachments',
                          name,
                          'conveyance_type',
                        ]);
                        const isAutre = attachmentType === ConveyanceAttachmentType.Autre;
                        const customTypeValue = formConveyance.getFieldValue([
                          'attachments',
                          name,
                          'custom_type',
                        ]);
                        return isAutre ? (
                          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Form.Item
                              {...restField}
                              label={t('fields.custom_type', { ns: 'attachments' })}
                              name={[name, 'custom_type']}
                              rules={[
                                { required: true, message: 'Veuillez saisir un type personnalisé pour "Autres"' },
                              ]}
                              initialValue={customTypeValue}
                            >
                              <Input maxLength={255} />
                            </Form.Item>
                          </Col>
                        ) : null;
                      })()}
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.details', { ns: 'attachments' })}
                          name={[name, 'details']}
                        >
                          <TextArea maxLength={500} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'document_number']}
                          label={t('fields.document_number', { ns: 'attachments' })}
                        >
                          <Input maxLength={255} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.issue_date', { ns: 'attachments' })}
                          name={[name, 'issue_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Form.Item
                          {...restField}
                          label={t('fields.expiration_date', { ns: 'attachments' })}
                          name={[name, 'expiration_date']}
                          getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                        >
                          <DatePicker
                            format="DD.MM.YYYY"
                            style={{ width: '100%' }}
                            disabled={conveyanceStatus === ConveyanceStatus.Granted}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Form.Item
                          {...restField}
                          label={t('fields.observations', { ns: 'attachments' })}
                          name={[name, 'observations']}
                        >
                          <TextArea maxLength={1000} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} style={{ textAlign: 'right' }}>
                        <Button
                          danger
                          onClick={() => handleRemoveAttachment(name, index, remove)}
                        >
                          {t('Remove Attachment')}
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add({
                          conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                          validation_status: AttachmentValidationStatus.waiting,
                          custom_type: null,
                        });
                        setConveyanceAttachments((prev) => [
                          ...prev,
                          {
                            uid: `attach-${fields.length}-${Date.now()}`,
                            name: '',
                            status: 'error' as const,
                            conveyance_type: ConveyanceAttachmentType.Acte_de_vente,
                            custom_type: null,
                          },
                        ]);
                      }}
                      block
                      icon={<i className="anticon anticon-plus" />}
                    >
                      {t('Add Attachment')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
